import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { goToLogin, hereOf } from '../../../../util/authReturn';
import { fetchMyGarage } from '../../../../ducks/myGarage.duck';
import { fetchFinanceStatus } from '../../../../ducks/financeStatus.duck';
import { getTradeIn } from '../../../../ducks/tradeIn.duck';
import {
  garageSummary,
  tradeInSummary,
  financeSummary,
} from '../../../../util/myTransactionSummaries';
import { TradeInForm } from '../../../../components';

import css from './MyTransactionMobileSection.module.css';

/**
 * The My Transaction cards, for the mobile menu.
 *
 * Same data and the same summary rules as the desktop panel (both render
 * from util/myTransactionSummaries), laid out as menu rows rather than a
 * dropdown grid so it reads like the rest of this menu.
 *
 * @param {boolean} props.isMenuOpen  whether the mobile menu is actually
 *   showing. This cannot be inferred from mounting: the menu lives inside a
 *   Modal that keeps its children mounted and hides them with CSS, so
 *   fetching on mount would hit the API on every page load for every
 *   signed-in visitor.
 */
export default function MyTransactionMobileSection({ isMenuOpen, isSignedIn }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const garage = useSelector(state => state.myGarage);
  const finance = useSelector(state => state.financeStatus);
  const currentUser = useSelector(state => state.user.currentUser);
  const [tradeInFormOpen, setTradeInFormOpen] = useState(false);

  const tradeIn = getTradeIn(currentUser);

  // One attempt per opening of the menu. A failed fetch leaves loaded=false,
  // which is the same condition that triggers a fetch, so without this an
  // outage would loop for as long as the menu stayed open.
  const attemptedRef = useRef(false);
  useEffect(() => {
    if (!isMenuOpen) {
      attemptedRef.current = false;
      return;
    }
    if (!isSignedIn || attemptedRef.current) return;
    attemptedRef.current = true;
    if (!garage.loaded && !garage.fetchInProgress) dispatch(fetchMyGarage());
    if (!finance.loaded && !finance.fetchInProgress) dispatch(fetchFinanceStatus());
  }, [
    isMenuOpen,
    isSignedIn,
    garage.loaded,
    garage.fetchInProgress,
    finance.loaded,
    finance.fetchInProgress,
    dispatch,
  ]);

  const go = path => () => history.push(path);

  const rows = [
    {
      icon: '🚗',
      label: 'My garage',
      summary: garageSummary(garage, isSignedIn),
      onClick: isSignedIn ? go('/listings') : () => goToLogin(history, '/listings'),
    },
    {
      icon: '🔄',
      label: 'My trade-in',
      summary: tradeInSummary(tradeIn, isSignedIn),
      onClick: isSignedIn
        ? () => setTradeInFormOpen(true)
        : () => goToLogin(history, hereOf(history)),
    },
    {
      icon: '💳',
      label: 'Finance',
      summary: financeSummary(finance, isSignedIn),
      onClick: isSignedIn ? go('/listings') : () => goToLogin(history, '/listings'),
    },
  ];

  return (
    <div className={css.root}>
      <p className={css.heading}>My Transaction</p>
      <ul className={css.rows}>
        {rows.map(row => (
          <li key={row.label}>
            <button type="button" className={css.row} onClick={row.onClick}>
              <span className={css.icon}>{row.icon}</span>
              <span className={css.text}>
                <span className={css.label}>{row.label}</span>
                {row.summary ? <span className={css.summary}>{row.summary}</span> : null}
              </span>
            </button>
          </li>
        ))}
      </ul>

      <TradeInForm isOpen={tradeInFormOpen} onClose={() => setTradeInFormOpen(false)} />
    </div>
  );
}
